import type { TonPastille } from '../components/ui/Primitives';
import type { StatutPiece } from './useApprenants';
import type { StatutDocument, StatutDocumentSigne } from './useDocuments';

/**
 * Pastilles d'état des pièces justificatives, partagées par les écrans qui les
 * affichent — dossier apprenant et inscriptions.
 *
 * Une deuxième copie de ces libellés dans l'écran des inscriptions aurait fini
 * par diverger de celle du dossier : le candidat y aurait lu « Vérifié » là où
 * le dossier annonce « À contrôler ». Les valeurs viennent de la maquette
 * validée.
 */
export const PASTILLE_PIECE: Record<StatutPiece, { texte: string; ton: TonPastille }> = {
  ATTENDUE: { texte: 'Non déposé', ton: 'danger' },
  DEPOSEE: { texte: 'À contrôler', ton: 'warning' },
  VERIFIEE: { texte: 'Vérifié', ton: 'info' },
  VALIDEE: { texte: 'Validé', ton: 'success' },
  REFUSEE: { texte: 'Refusé', ton: 'danger' },
};

/**
 * Pastilles d'état des documents produits, partagées par les écrans qui les
 * affichent — documents réglementaires et dossier apprenant. Même raison que
 * ci-dessus : deux copies auraient fini par diverger.
 */
export const PASTILLE_DOCUMENT: Record<StatutDocument, { texte: string; ton: TonPastille }> = {
  GENERE: { texte: 'Généré', ton: 'info' },
  VERIFIE: { texte: 'Vérifié', ton: 'warning' },
  VALIDE: { texte: 'Validé', ton: 'success' },
  TRANSMIS: { texte: 'Transmis', ton: 'mint' },
};

/**
 * Pastilles de l'exemplaire signé déposé en retour. Volontairement distinctes
 * de celles du document produit : « Validé » ne veut pas dire la même chose
 * d'un document généré et d'un exemplaire signé rendu par l'antenne.
 */
export const PASTILLE_DOCUMENT_SIGNE: Record<
  StatutDocumentSigne,
  { texte: string; ton: TonPastille }
> = {
  DEPOSE: { texte: 'À contrôler', ton: 'warning' },
  VALIDE: { texte: 'Signé validé', ton: 'success' },
  REFUSE: { texte: 'Signé refusé', ton: 'danger' },
};

/**
 * État de signature d'un document produit, y compris avant tout dépôt.
 *
 * L'absence de pastille ne se lit pas : « rien » se confond avec « pas
 * concerné ». Un document sans exemplaire signé annonce donc « Non signé », en
 * ton neutre — c'est l'état normal d'un document qui vient d'être généré, pas
 * une anomalie à signaler en rouge.
 */
export function pastilleSignature(statut: StatutDocumentSigne | null | undefined): {
  texte: string;
  ton: TonPastille;
} {
  return statut ? PASTILLE_DOCUMENT_SIGNE[statut] : { texte: 'Non signé', ton: 'neutral' };
}
